import React, { useState, useEffect } from 'react';
import { 
  Users, 
  DollarSign, 
  Clock, 
  TrendingUp, 
  AlertTriangle, 
  Car, 
  Navigation, 
  MapPin,
  Calendar,
  Award,
  Building,
  Timer,
  Route,
  CheckCircle,
  XCircle,
  Activity,
  Play,
  Pause,
  StopCircle,
  Wifi,
  WifiOff,
  Phone,
  Wrench,
  Home,
  Truck,
  ExternalLink,
  User
} from 'lucide-react';

const TestNewLayout = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedTech, setSelectedTech] = useState(null);

  // Update current time every 30 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  // Real-time technician tracking data
  const technicianData = [
    {
      id: 1,
      name: "John Smith",
      employeeId: "20001",
      adpStatus: "clocked_in",
      adpClockTime: "07:00 AM",
      currentLocation: "1234 Main St, Downtown",
      vehicleStatus: "Engine off",
      currentJob: {
        customer: "ABC Corporation",
        address: "1234 Main St, Downtown",
        dispatched: "08:00 AM",
        azuga_arrival: "08:55 AM",
        st_arrived: "09:00 AM",
        azuga_depart: null,
        st_done: null
      },
      distanceFromJob: "0.1 mi",
      discrepancies: [],
      gpsStatus: "connected"
    },
    {
      id: 2,
      name: "Sarah Johnson", 
      employeeId: "20023",
      adpStatus: "clocked_in",
      adpClockTime: "07:30 AM",
      currentLocation: "Main St & 5th Ave",
      vehicleStatus: "Driving",
      currentJob: {
        customer: "Smith Residence",
        address: "567 Oak Ave, Midtown",
        dispatched: "09:30 AM",
        azuga_arrival: null,
        st_arrived: null,
        azuga_depart: null,
        st_done: null
      },
      distanceFromJob: "2.1 mi",
      discrepancies: [],
      gpsStatus: "connected"
    },
    {
      id: 3,
      name: "Mike Wilson",
      employeeId: "20045", 
      adpStatus: "not_clocked_in",
      adpClockTime: null,
      currentLocation: "Unknown - GPS offline",
      vehicleStatus: "Unknown",
      currentJob: {
        customer: "Wilson Manufacturing",
        address: "890 Industrial Blvd",
        dispatched: "08:00 AM",
        azuga_arrival: null,
        st_arrived: null,
        azuga_depart: null,
        st_done: null
      },
      distanceFromJob: "Unknown",
      discrepancies: [],
      gpsStatus: "disconnected"
    },
    {
      id: 4,
      name: "Lisa Brown",
      employeeId: "20067",
      adpStatus: "on_lunch",
      adpClockTime: "07:15 AM",
      lunchStartTime: "12:00 PM",
      earlierPunches: [
        { start: "07:15 AM", end: "12:00 PM" }
      ],
      currentLocation: "Office - 123 Company Way",
      vehicleStatus: "Engine off",
      currentJob: {
        customer: "Blue Inc",
        address: "456 Business Park Dr",
        dispatched: "08:30 AM",
        azuga_arrival: "09:15 AM",
        st_arrived: "09:20 AM",
        azuga_depart: "11:45 AM",
        st_done: "11:50 AM"
      },
      earlierJobs: [
        {
          customer: "Green Corp",
          dispatched: "07:30 AM",
          azuga_arrival: "08:00 AM",
          st_arrived: "08:05 AM",
          azuga_depart: "08:25 AM",
          st_done: "08:30 AM"
        }
      ],
      distanceFromJob: "N/A",
      discrepancies: [],
      gpsStatus: "connected"
    },
    {
      id: 5,
      name: "Tom Rodriguez",
      employeeId: "20089",
      adpStatus: "clocked_in",
      adpClockTime: "07:45 AM",
      currentLocation: "Highway 101 & Oak St",
      vehicleStatus: "Driving",
      currentJob: {
        customer: "Metro Systems",
        address: "789 Tech Center Blvd",
        dispatched: "08:45 AM",
        azuga_arrival: "09:30 AM",
        st_arrived: "09:35 AM",
        azuga_depart: "10:00 AM",
        st_done: null
      },
      distanceFromJob: "3.2 mi",
      discrepancies: [],
      checkoutDiscrepancy: {
        type: "checkout",
        message: "did not close job",
        severity: "medium",
        timestamp: new Date(Date.now() - 20 * 60000),
        distance_feet: 1680,
        actual_time: "10:15 AM",
        expected_time: "10:00 AM"
      },
      gpsStatus: "connected"
    },
    {
      id: 6,
      name: "Jennifer Lee",
      employeeId: "21065",
      adpStatus: "clocked_in",
      adpClockTime: "07:20 AM",
      currentLocation: "Parking lot - 555 Commerce Dr",
      vehicleStatus: "Idle",
      currentJob: {
        customer: "DataFlow Corp",
        address: "555 Commerce Dr",
        dispatched: "08:15 AM",
        azuga_arrival: "09:00 AM",
        st_arrived: "09:05 AM",
        azuga_depart: null,
        st_done: null
      },
      distanceFromJob: "0.0 mi",
      discrepancies: [],
      idleDiscrepancy: {
        type: "excessive_idle_time",
        message: "excessive_idle_time",
        severity: "medium",
        timestamp: new Date(Date.now() - 65 * 60000),
        context_data: {
          idle_duration_minutes: 65,
          idle_start_time: "9:00 AM",
          idle_end_time: "10:05 AM",
          idle_address: "Parking lot - 555 Commerce Dr"
        },
        distance_feet: 150
      },
      gpsStatus: "connected"
    }
  ];

  // Helper functions
  const formatDistance = (distanceFeet) => {
    if (distanceFeet == null) return 'N/A';
    
    const miles = distanceFeet / 5280;
    
    if (miles < 0.1) {
      return `${distanceFeet.toFixed(0)} ft`;
    }
    
    return `${miles.toFixed(2)} mi`;
  };

  const formatMinutesToHours = (minutes) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}hr${remainingMinutes}m` : `${hours}hr`;
  };

  const isLateArrival = (stArrived, azugaArrival) => {
    if (!stArrived || !azugaArrival) return false;
    
    const parseTime = (timeStr) => {
      const [time, period] = timeStr.split(' ');
      const [hours, minutes] = time.split(':').map(Number);
      const adjustedHours = period === 'PM' && hours !== 12 ? hours + 12 : 
                           period === 'AM' && hours === 12 ? 0 : hours;
      return adjustedHours * 60 + minutes;
    };
    
    const stMinutes = parseTime(stArrived);
    const azugaMinutes = parseTime(azugaArrival);
    
    return azugaMinutes - stMinutes > 10;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'on_job': return 'bg-green-100 text-green-800 border-green-200';
      case 'driving': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'break': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'not_clocked_in': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'on_job': return <Wrench className="h-4 w-4" />;
      case 'driving': return <Car className="h-4 w-4" />;
      case 'break': return <Pause className="h-4 w-4" />;
      case 'not_clocked_in': return <StopCircle className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  const getAdpStatusColor = (status) => {
    switch (status) {
      case 'clocked_in': return 'text-green-600';
      case 'on_lunch': return 'text-yellow-600';
      case 'not_clocked_in': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diffMs = now - timestamp;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours}h ${diffMins % 60}m ago`;
  };

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600';
      case 'in_progress': return 'text-blue-600';
      case 'en_route': return 'text-purple-600';
      case 'scheduled': return 'text-gray-600';
      case 'missed': return 'text-red-600';
      case 'at_risk': return 'text-orange-600';
      default: return 'text-gray-600';
    }
  };

  // Count discrepancies by severity
  const discrepancyCounts = technicianData.reduce((acc, tech) => {
    tech.discrepancies.forEach(disc => {
      acc[disc.severity] = (acc[disc.severity] || 0) + 1;
      acc.total = (acc.total || 0) + 1;
    });
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Real-Time Technician Tracking</h1>
              <p className="text-gray-600 mt-1">Live status, locations, and discrepancy monitoring</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Current time: {currentTime.toLocaleTimeString()}
              </div>
              <div className="flex items-center text-sm">
                <div className="h-2 w-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                Live tracking
              </div>
            </div>
          </div>
        </div>


        {/* Technician Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Live Technician Status</h2>
            <p className="text-sm text-gray-600">Real-time tracking and discrepancy monitoring</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Technician
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ADP Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Job
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {technicianData
                  .sort((a, b) => {
                    // Sort by discrepancies first (those with discrepancies at top)
                    const aHasDiscrepancies = a.discrepancies.length > 0 || a.idleDiscrepancy || a.checkoutDiscrepancy;
                    const bHasDiscrepancies = b.discrepancies.length > 0 || b.idleDiscrepancy || b.checkoutDiscrepancy;
                    
                    if (aHasDiscrepancies && !bHasDiscrepancies) return -1;
                    if (!aHasDiscrepancies && bHasDiscrepancies) return 1;
                    
                    // Then by name alphabetically
                    return a.name.localeCompare(b.name);
                  })
                  .map((tech) => (
                  <>
                    <tr 
                      key={tech.id} 
                      className={`${
                        tech.adpStatus === 'not_clocked_in'
                        ? 'bg-gray-50 border-l-4 border-gray-300'
                        : tech.discrepancies.length > 0 || tech.idleDiscrepancy || tech.checkoutDiscrepancy
                        ? 'bg-orange-50'
                        : ''
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{tech.name}</div>
                          <div className="text-sm text-gray-500">{tech.employeeId}</div>
                          {tech.discrepancies.length > 0 && (
                            <div className="mt-2 space-y-2">
                              {tech.discrepancies.map((disc, idx) => (
                                <div key={idx} className="text-xs text-red-700">
                                  {disc.type === 'checkout' ? (
                                    <div className="space-y-1">
                                      <div className="font-medium">Left job at {disc.expected_time} without closing</div>
                                    </div>
                                  ) : (
                                    <div>{disc.message}</div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {tech.adpStatus === 'on_lunch' ? (
                          <div className="space-y-1">
                            <div className="text-xs text-gray-500">{tech.adpClockTime} - {tech.lunchStartTime}</div>
                            <div className={`text-sm font-medium ${getAdpStatusColor(tech.adpStatus)}`}>
                              ON LUNCH
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className={`text-sm font-medium ${getAdpStatusColor(tech.adpStatus)}`}>
                              {tech.adpStatus.replace('_', ' ').toUpperCase()}
                            </div>
                            {tech.adpClockTime && (
                              <div className="text-xs text-gray-500">since {tech.adpClockTime}</div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="text-sm text-gray-900">{tech.currentLocation}</div>
                          {tech.vehicleStatus !== 'Idle' && (
                            <div className="text-xs text-gray-600">{tech.vehicleStatus}</div>
                          )}
                          {tech.currentJob && tech.distanceFromJob !== "0.0 mi" && (
                            <div className="text-xs text-gray-500">{tech.distanceFromJob} from job site</div>
                          )}
                          {tech.idleDiscrepancy && (
                            <div className="mt-2 text-xs text-red-700 space-y-1">
                              <div className="font-medium">Excessive Idle: {formatMinutesToHours(Math.round(tech.idleDiscrepancy.context_data.idle_duration_minutes))}</div>
                              <div>{tech.idleDiscrepancy.context_data.idle_start_time} - {tech.idleDiscrepancy.context_data.idle_end_time} ({formatDistance(tech.idleDiscrepancy.distance_feet)} from job site)</div>
                            </div>
                          )}
                          {tech.checkoutDiscrepancy && (
                            <div className="mt-2 text-xs text-red-700">
                              <div className="font-medium">Left job at {tech.checkoutDiscrepancy.expected_time} without closing</div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {tech.currentJob ? (
                          <div className="space-y-1 text-xs">
                            {tech.currentJob.dispatched && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Dispatched:</span>
                                <span className="font-medium">{tech.currentJob.dispatched}</span>
                              </div>
                            )}
                            {tech.currentJob.azuga_arrival && (
                              <div className={`flex justify-between ${isLateArrival(tech.currentJob.st_arrived, tech.currentJob.azuga_arrival) ? 'text-red-700' : ''}`}>
                                <span className={`${isLateArrival(tech.currentJob.st_arrived, tech.currentJob.azuga_arrival) ? 'text-red-700' : 'text-gray-600'}`}>Azuga Arrival:</span>
                                <span className={`font-medium ${isLateArrival(tech.currentJob.st_arrived, tech.currentJob.azuga_arrival) ? 'text-red-700' : ''}`}>{tech.currentJob.azuga_arrival}</span>
                              </div>
                            )}
                            {tech.currentJob.st_arrived && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">ST Arrived:</span>
                                <span className="font-medium">{tech.currentJob.st_arrived}</span>
                              </div>
                            )}
                            {tech.currentJob.azuga_depart && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Azuga Depart:</span>
                                <span className="font-medium">{tech.currentJob.azuga_depart}</span>
                              </div>
                            )}
                            {tech.currentJob.st_done && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">ST Done:</span>
                                <span className="font-medium">{tech.currentJob.st_done}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No active job</span>
                        )}
                      </td>
                    </tr>
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestNewLayout;
